import type { ListingDetails } from 'app/types/listingDetails.type';
import numberUtils from 'app/shared/utils/numberUtils';
import schemaUtils from 'app/shared/utils/schemaUtils';

const DOMAIN = 'https://hotpads.com';
const UNDISCLOSED_ADDRESS = '(Undisclosed Address)';

// Backends sometimes set `displayName` to the street address itself. For undisclosed
// listings we must scrub it so the street doesn't leak through JSON-LD, meta tags, or
// alt text. Returns a safe label ("(Undisclosed Address)") when `hideStreet` is true.
export function getPublicListingName(listing: Pick<ListingDetails, 'displayName' | 'address'>): string {
  if (listing.address?.hideStreet) {
    return UNDISCLOSED_ADDRESS;
  }
  return listing.displayName || '';
}

// --- Page Title ---

export function getPageTitle(listing: ListingDetails): string {
  const { address, displayName, isApartmentBldg, pricing, unit } = listing;
  if (!address) return `${displayName || 'Listing'} | HotPads`;
  const { street, city, state, zip, hideStreet } = address;

  const noAddress = `${hideStreet ? UNDISCLOSED_ADDRESS : displayName}, ${city}, ${state} ${zip}`;
  const formattedAddress = hideStreet ? noAddress : `${street}, ${city}, ${state} ${zip}`;
  const formattedAddressWithApt = hideStreet ? noAddress : `${street} Apt ${unit}, ${city}, ${state} ${zip}`;

  let title = '';

  // The apartment-bldg branch embeds `displayName` in the title. For undisclosed listings the
  // backend may set `displayName` to the street address, so fall through to the scrubbed
  // address path when hideStreet is true.
  if (isApartmentBldg && displayName !== street && !hideStreet) {
    const containsApartment = /apartment/gi.test(displayName);
    const priceSummary = pricing?.summary;
    const priceLowOrHigh = priceSummary?.priceLow || priceSummary?.priceHigh;
    const tabTitlePrice = priceLowOrHigh ? ` - ${numberUtils.asCurrency(priceLowOrHigh)}+ per month` : '';

    const appendKeyword = containsApartment ? ` in ${city}` : ` Apartments in ${city}`;
    const formattedDisplayName = displayName + appendKeyword;

    title = formattedDisplayName.length <= 40 ? formattedDisplayName + tabTitlePrice : formattedDisplayName;
  } else if (unit) {
    title = formattedAddressWithApt;
  } else {
    title = formattedAddress;
  }

  return `${title} | HotPads`;
}

// --- Robots / NoIndex ---

export function shouldNoIndex(listing: ListingDetails): boolean {
  if (!listing.uriV2) return true;
  if (listing.propertyType === 'land') return true;
  return false;
}

// --- Structured Data (JSON-LD) ---

type SchemaGraph = {
  '@context': string;
  '@graph': Array<Record<string, unknown>>;
};

function getPropertySchemaType(propertyType: string): string {
  switch (propertyType) {
    case 'house':
    case 'townhouse':
      return 'SingleFamilyResidence';
    case 'condo':
      return 'Apartment';
    case 'room':
      return 'Room';
    default:
      return 'ApartmentComplex';
  }
}

function buildBreadcrumbList(listing: ListingDetails): Record<string, unknown> | null {
  const breadcrumbs = listing.areas?.breadcrumbs;
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  let position = 0;
  const items = breadcrumbs.map((crumb) => ({
    '@type': 'ListItem',
    position: ++position,
    item: {
      '@type': 'Thing',
      '@id': DOMAIN + crumb.uriV2,
      name: crumb.name,
    },
  }));

  items.push({
    '@type': 'ListItem',
    position: ++position,
    item: {
      '@type': 'Thing',
      '@id': DOMAIN + listing.uriV2,
      name: getPublicListingName(listing),
    },
  });

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function buildMainEntity(listing: ListingDetails): Record<string, unknown> {
  const schemaType = getPropertySchemaType(listing.propertyType);
  const { street, city, state, zip, hideStreet } = listing.address || {};
  const { lat, lon } = listing.geo || {};
  const { minPrice, maxPrice } = listing.listingMinMaxPriceBeds || {};
  const fullUrl = DOMAIN + listing.uriV2;
  const publicName = getPublicListingName(listing);

  const address = {
    '@type': 'PostalAddress',
    streetAddress: hideStreet ? undefined : street,
    addressLocality: city,
    addressRegion: state,
    postalCode: zip,
    addressCountry: { '@type': 'Country', name: 'US' },
  };

  const geo = {
    '@type': 'GeoCoordinates',
    latitude: lat,
    longitude: lon,
  };

  const amenityFeature =
    listing.amenities?.amenities?.map((amenity: string) => ({
      '@type': 'LocationFeatureSpecification',
      name: amenity,
    })) ?? null;

  const phone = !listing.building && listing.listedBy ? listing.listedBy.contactPhone : null;

  return {
    '@type': schemaType,
    '@id': fullUrl,
    name: publicName,
    url: fullUrl,
    telephone: phone,
    description: listing.details?.fullDescription,
    address,
    geo,
    image: listing.previewPhoto?.url,
    amenityFeature,
    containedInPlace: {
      '@type': 'LocalBusiness',
      '@id': fullUrl,
      name: publicName,
      priceRange: `$${minPrice}-$${maxPrice}`,
      image: listing.previewPhoto?.url,
      telephone: phone,
      address,
    },
  };
}

function buildProduct(listing: ListingDetails): Record<string, unknown> {
  const { minPrice, maxPrice } = listing.listingMinMaxPriceBeds;
  const availability = schemaUtils.getAvailability(listing as unknown as Record<string, unknown>);

  const offers: Record<string, unknown> = {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: minPrice,
    highPrice: maxPrice,
  };

  if (availability) {
    offers.availability = availability;
  }

  return {
    '@type': 'Product',
    name: getPublicListingName(listing),
    description: listing.details?.fullDescription,
    image: listing.previewPhoto?.url,
    offers,
  };
}

export function getStructuredData(listing: ListingDetails): SchemaGraph {
  const graph: Array<Record<string, unknown>> = [];

  const breadcrumb = buildBreadcrumbList(listing);
  const mainEntity = buildMainEntity(listing);
  const about = listing.trusted ? null : buildProduct(listing);

  graph.push({
    '@type': 'ItemPage',
    breadcrumb,
    mainEntity,
    about,
  });

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
